type SupportedMediaElement = HTMLVideoElement;

// Type guard for checking if element is a supported media type
const isSupportedMediaElement = (element: any): element is SupportedMediaElement => {
	return element instanceof HTMLVideoElement;
};

// Centralized map for debounce timers
const pauseDebounceTimers = new Map<string, NodeJS.Timeout>();

/**
 * Video control utility functions
 */
export const VideoControls = {
	/**
	 * Play video with autoplay fallback
	 */
	play: async (element: any): Promise<boolean> => {
		if (!isSupportedMediaElement(element)) {
			console.warn('Unsupported media element. Only HTMLVideoElement is currently supported.');
			return false;
		}

		// Check if video has buffered enough data
		if (element.readyState < 3) {
			// HAVE_FUTURE_DATA
			return new Promise((resolve) => {
				const canPlayHandler = async () => {
					element.removeEventListener('canplay', canPlayHandler);
					try {
						await element.play();
						resolve(true);
					} catch (error) {
						if (error instanceof Error && error.name === 'NotAllowedError') {
							element.muted = true;
							try {
								await element.play();
								resolve(true);
							} catch (innerError) {
								resolve(false);
							}
						} else {
							console.error('Error playing video:', error);
							resolve(false);
						}
					}
				};

				element.addEventListener('canplay', canPlayHandler);

				// If it becomes ready while we were setting up
				if (element.readyState >= 3) {
					element.removeEventListener('canplay', canPlayHandler);
					canPlayHandler();
				}
			});
		}

		// If the video is ready to play, play it
		try {
			await element.play();
			return true;
		} catch (error) {
			if (error instanceof Error && error.name === 'NotAllowedError') {
				// Try with muted if autoplay was blocked
				element.muted = true;
				try {
					await element.play();
					return true;
				} catch (innerError) {
					console.error('Failed to play video even when muted:', innerError);
					return false;
				}
			} else {
				console.error('Error playing video:', error);
				return false;
			}
		}
	},

	/**
	 * Debounced pause with timeout clearing - always use this instead of direct pause
	 */
	pause: (element: any, callback?: () => void, delay = 100) => {
		if (!isSupportedMediaElement(element)) {
			console.warn('Unsupported media element. Only HTMLVideoElement is currently supported.');
			return;
		}

		if (pauseDebounceTimers.has(element.id)) {
			clearTimeout(pauseDebounceTimers.get(element.id));
		}

		const timerId = setTimeout(() => {
			if (!element.paused) {
				element.pause();
				callback?.();
			}
			pauseDebounceTimers.delete(element.id);
		}, delay);

		pauseDebounceTimers.set(element.id, timerId);
	},

	/**
	 * Checks if an element is a supported media type
	 */
	validateMediaElement: (element: any): boolean => {
		const isValid = isSupportedMediaElement(element);
		if (!isValid) {
			console.warn('Unsupported media element. Only HTMLVideoElement is currently supported.');
		}
		return isValid;
	},
};
